import { Stack, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import SlotComponent from '~/components/BookingTimeSelector/Views/components/SlotStates';
import { SlotShape, VisitModalities } from '~/shapes/Slot';
import { LocationWithCareTypes } from '~/components/CareTeam/hooks/useCareTeam';
import history from '~/core/history';
import { analyticsTrackEvent } from '~/core/analytics';
import { ACCOUNT_HOME_CLICK_BOOK_AGAIN_SLOT } from '~/core/analytics/events';
import { colors } from '~/constants/colors';

const StyledSlot = styled(SlotComponent).withConfig({ componentId: 'sc-qhmwbz-0' })`
  width: 88px;
  height: 44px;
  line-height: 20px;
  font-size: 14px;
`;

type Props = {
  location: LocationWithCareTypes;
  slotsData: SlotShape[];
};

/**
 * Returns an array containing up to three "featured" slots from the given array of slots.
 *
 * @param {SlotShape[]} slotsData - The array of slots to process.
 * @returns {SlotShape[]} An array containing up to three featured slots.
 *
 * Here's the logic for determining which slots are "featured":
 * 1. The first slot is the earliest slot.
 * 2. The second slot is the earliest slot with the opposite modality of the first slot.
 *    (If there are no slots with the opposite modality, it's just the second-earliest slot.)
 * 3. The third slot is the earliest remaining slot. (Note: this can be earlier than the second slot,
 *    since there's no modality requirement!)
 */
function getFeaturedSlots(slotsData: SlotShape[]): SlotShape[] {
  const sortedSlots = slotsData.sort(
    (a, b) => +new Date(a.appointmentDate) - +new Date(b.appointmentDate)
  );

  const firstFeaturedSlot = sortedSlots[0];

  let secondFeaturedSlot: SlotShape | undefined;
  if (sortedSlots.length > 1) {
    const modalityToFind =
      firstFeaturedSlot.modality === VisitModalities.InPerson
        ? VisitModalities.Telemed
        : VisitModalities.InPerson;
    const earliestSlotWithOtherModality = sortedSlots.find(
      (slot) => slot.modality === modalityToFind
    );

    secondFeaturedSlot = earliestSlotWithOtherModality || sortedSlots[1];
  }

  const thirdFeaturedSlot =
    sortedSlots.length > 2
      ? sortedSlots.find((slot) => slot !== firstFeaturedSlot && slot !== secondFeaturedSlot)
      : undefined;

  return [firstFeaturedSlot, secondFeaturedSlot, thirdFeaturedSlot].filter(Boolean) as SlotShape[];
}

/**
 * Fetches and displays the next three time slots for a location.
 *
 */
const FeaturedSlots = ({ location, slotsData }: Props) => {
  const featuredSlots = getFeaturedSlots(slotsData);

  const handleSelectSlot = (slot: SlotShape, location: LocationWithCareTypes, i: number) => {
    const isBookingAssociatedTelemedSlot =
      slot.modality === 'telemed' && !!location.associatedTelemedLocationId;
    const selectedLocationId = isBookingAssociatedTelemedSlot
      ? location.associatedTelemedLocationId
      : location.id;

    const appointmentTime = +new Date(slot.appointmentDate);
    history.push(
      `/book/direct/${selectedLocationId}?appointmentTime=${appointmentTime}&o=/account/home`,
      {
        from: 'account',
      }
    );
    analyticsTrackEvent(ACCOUNT_HOME_CLICK_BOOK_AGAIN_SLOT, {
      locationOnCard: location.id,
      lastBooked: location.lastBooked,
      slotNumber: i + 1,
      isBookingAssociatedTelemedSlot,
    });
  };

  return (
    <Stack space={0.5}>
      <Stack row space={2} sx={{ color: colors.gray300 }}>
        <hr style={{ width: '100%' }} />
        <Text muted variant="body2">
          Today
        </Text>
        <hr style={{ width: '100%' }} />
      </Stack>
      <Stack justifyContent="center" row space={0.5}>
        {!isEmpty(featuredSlots) && (
          <Stack row space={1}>
            {featuredSlots?.map((slot, i) => (
              <StyledSlot
                isCTA
                key={`${slot.appointmentDate} - ${i}`}
                onSelect={() => handleSelectSlot(slot, location, i)}
                showModalitySubtitle
                slot={slot}
                timeZone={location.timeZone}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default FeaturedSlots;
